"use client"

import { badgeVariants } from "@/components/ui/badge"
import { STATE_TONE_STYLES } from "@/components/ui/conversation/thread-tones"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  ESCALATION_CATEGORY_LABELS,
  HUMAN_OUTCOME_LABELS,
  OUTCOME_LABELS,
  OUTCOME_TONES,
} from "@/lib/conversation-quality/outcome-display"
import { OUTCOME_METRIC_DEFINITIONS } from "@/lib/conversation-quality/metric-definitions"
import type { OutcomeSource } from "@/lib/conversation-quality/outcomes"
import type { ThreadQuality, ThreadTone } from "@/lib/conversation/thread-header"
import { cn } from "@/lib/utils"

/**
 * How the last judged session went, beside the conversation's own state badges.
 *
 * The badge is also the way to fix it: clicking the verdict to correct the verdict beats
 * hunting for an unrelated entry in the overflow menu. Where the operator may not correct
 * it, the same badge is not pressable.
 */
export function SessionOutcomeBadge({
  quality,
  onCorrect,
}: {
  quality: ThreadQuality
  onCorrect?: () => void
}) {
  // The flag is the point of the review queue, so it outranks the outcome's own tone.
  const tone: ThreadTone = quality.needsReview ? "warning" : OUTCOME_TONES[quality.outcome]
  const className = cn(
    badgeVariants({ variant: "outline" }),
    "whitespace-nowrap px-1.5 py-0 text-[10px]",
    STATE_TONE_STYLES[tone],
    onCorrect && "cursor-pointer hover:brightness-95"
  )
  const label = quality.needsReview
    ? `${OUTCOME_LABELS[quality.outcome]} · check`
    : OUTCOME_LABELS[quality.outcome]

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          {onCorrect ? (
            <button type="button" className={className} onClick={onCorrect}>
              {label}
            </button>
          ) : (
            <span className={className}>{label}</span>
          )}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-1 text-xs">
          {describe(quality).map((line) => (
            <p key={line}>{line}</p>
          ))}
          {onCorrect && <p className="text-muted-foreground">Click to correct it.</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** What the badge cannot fit: which session, who decided, and the parts of an escalation. */
function describe(quality: ThreadQuality): string[] {
  const lines = [`Session #${quality.sessionNumber} · ${SOURCE_LABELS[quality.outcomeSource]}`]

  if (quality.escalationCategory) {
    lines.push(`Handed over: ${ESCALATION_CATEGORY_LABELS[quality.escalationCategory]}`)
  }
  if (quality.humanOutcome) {
    lines.push(`${OUTCOME_METRIC_DEFINITIONS.handoverOutcomes.label}: ${HUMAN_OUTCOME_LABELS[quality.humanOutcome]}`)
  }
  if (quality.needsReview) lines.push("Waiting for someone to confirm it.")

  return lines
}

const SOURCE_LABELS: Record<OutcomeSource, string> = {
  rule: "decided by rule",
  ai: "decided by AI",
  manual: "corrected by a person",
}
